import { useMemo } from 'react';
import { ShopifyProduct, ShopifyVariant, useShopifyCart, useRecentlyViewed } from '@/hooks';
import { useFavorites } from "@/contexts/FavoritesContext";
import { UnifiedProductCard } from '@/components/UnifiedProductCard';
import { getImageIndexForVariant } from '@/utils/imageVariantSync';
import TimelessBadge from './TimelessBadge';
import { useStandardQuickView } from '@/hooks/useStandardQuickView';

interface ShopifyProductCardProps {
  product: ShopifyProduct;
  size?: 'large' | 'medium' | 'small';
  redirectOnAdd?: boolean;
  matchedVariant?: ShopifyVariant | null;
  columnCount?: 2 | 3 | 4;
  viewSize?: 'small' | 'medium' | 'large';
  showPurchaseControls?: boolean;
  onImageLoad?: () => void;
}

export const ShopifyProductCard = ({ product, size = 'large', redirectOnAdd = false, matchedVariant, columnCount, viewSize = 'large', showPurchaseControls = false, onImageLoad }: ShopifyProductCardProps) => {
  const { handleQuickView } = useStandardQuickView();
  const { addToCart } = useShopifyCart();
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();

  console.log('🎴 ShopifyProductCard rendering:', product.title);
  console.log('🖼️ Product images:', product.images?.length, 'images available');
  console.log('🔗 Image URLs:', product.images?.map(img => img.url));

  // Use matched variant as default if provided, otherwise use first variant
  const defaultVariant = matchedVariant || product.variants[0];
  
  // OFFICIAL IMAGE SYNC: Use unified system instead of manual fallbacks
  const imageResult = useMemo(() => {
    if (!product.images?.length) return { imageIndex: 0 };
    
    // NO FILTERS: Always show first Shopify image (index 0)
    if (!matchedVariant) {
      return { imageIndex: 0 };
    }
    
    // FILTERS APPLIED: Use variant matching logic to show variant's image
    return getImageIndexForVariant({
      product,
      selectedVariant: matchedVariant,
      currentImageIndex: 0
    });
  }, [product, matchedVariant]);
  
  const currentImage = product.images?.[imageResult.imageIndex] || defaultVariant?.image;

  const formatPrice = (amount: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(amount));
  };

  const handleAddToCart = (variantId?: string) => {
    const variant = product.variants.find(v => v.id === variantId) || product.variants[0];
    if (variant) {
      addToCart(product, variant, 1);
    }
  };

  const handleToggleFavorite = () => {
    if (isFavorite(product.id)) {
      removeFromFavorites(product.id);
    } else {
      addToFavorites(product.id, product.title, currentImage?.url || '', formatPrice(defaultVariant.price.amount));
    }
  };

  const handleQuickViewClick = () => {
    console.log('🔍 ShopifyProductCard: QuickView clicked for product:', product.title);
    console.log('🔍 ShopifyProductCard: handleQuickView function exists:', !!handleQuickView);
    handleQuickView(product);
  };

  // Create badges array
  const badges: Array<{label: string; variant: 'destructive' | 'secondary' | 'outline'; className?: string}> = [];
  
  // Add TimelessBadge badges (simplified for now - can be enhanced)
  if (!size || size === 'large') {
    if (product.tags?.includes('featured')) {
      badges.push({ label: 'Featured', variant: 'secondary', className: 'bg-primary text-primary-foreground' });
    }
  }

  // Convert variants for UnifiedProductCard, filtering out generic Shopify defaults
  const hasRealVariants = product.variants.length > 1 || 
    (product.variants.length === 1 && 
     product.variants[0].title !== 'Default Title' && 
     !product.variants[0].selectedOptions?.some(opt => 
       opt.name === 'Title' || opt.value === 'Default Title'
     )
    );

  const unifiedVariants = hasRealVariants ? product.variants.map(variant => ({
    id: variant.id,
    title: variant.title,
    price: formatPrice(variant.price.amount),
    compareAtPrice: variant.compareAtPrice ? formatPrice(variant.compareAtPrice.amount) : undefined,
    image: variant.image?.url,
    quantityAvailable: variant.quantityAvailable
  })) : [];


  return (
    <>
      <UnifiedProductCard
        id={product.id}
        title={product.title}
        description={product.description}
        image={currentImage?.url || '/placeholder.svg'}
        imageAlt={currentImage?.altText || `${product.title}${defaultVariant?.title ? ' - ' + defaultVariant.title : ''}`}
        price={formatPrice(defaultVariant.price.amount)}
        compareAtPrice={defaultVariant.compareAtPrice ? formatPrice(defaultVariant.compareAtPrice.amount) : undefined}
        productUrl={`/shopify-product/${product.handle}`}
        variants={unifiedVariants}
        size={size}
        columnCount={columnCount}
        viewSize={viewSize}
        showPurchaseControls={showPurchaseControls}
        onAddToCart={handleAddToCart}
        onToggleFavorite={handleToggleFavorite}
        onQuickView={handleQuickViewClick}
        isFavorite={isFavorite(product.id)}
        availableForSale={product.availableForSale}
        badges={badges}
        tags={product.tags?.filter(Boolean) || []}
        onImageLoad={onImageLoad}
      />

    </>
  );
};